import React, { useState, useRef } from 'react';
import VendorBilling from './components/VendorBilling';
import AttritionReport from './components/AttritionReport';
import UnregisteredCandidates from './components/UnregisteredCandidates';
import StaffingCompanyDashboard from './StaffingCompanyDashboard';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Container,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Paper,
  Stack,
  Alert,
  CircularProgress,
  Badge
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Work as WorkIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Logout as LogoutIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  GetApp as GetAppIcon,
  Assessment as AssessmentIcon,
  Receipt as ReceiptIcon,
  PendingActions as PendingIcon
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL ;
const QUALIFICATIONS = ['10th pass', '12th pass', 'ITI', 'Diploma', 'Graduate'];

// Helper to get the full logo URL
const getLogoUrl = (logo_url) => {
  if (!logo_url) return '';
  if (logo_url.startsWith('http')) return logo_url;
  return `${API_URL}/uploads/logos/${logo_url}`;
};

// Using Material-UI components instead of custom ones

function CompanyDashboard({ onLogout }) {
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('gogig_user')) || {});
  const isStaffingCompany = user?.company_type === 'staffing';
  const [activeTab, setActiveTab] = useState('dashboard');
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({
    company_name: user.company_name || '',
    address: user.address || '',
    logo_url: user.logo_url || '',
    email: user.email || '',
    establishment_code: user.establishment_code || '',
    logo: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [jobs, setJobs] = useState([]);
  const [jobApplicantCounts, setJobApplicantCounts] = useState({}); // Store applicant counts per job
  const [jobForm, setJobForm] = useState({
    title: '',
    description: '',
    age_criteria: '',
    stipend_min: '',
    stipend_max: '',
    location: '',
    qualification: [],
  });
  const [jobError, setJobError] = useState('');
  const [jobSuccess, setJobSuccess] = useState('');
  const [editJobId, setEditJobId] = useState(null);
  const [showJobForm, setShowJobForm] = useState(false);
  const [showApplicants, setShowApplicants] = useState(false);
  const [applicants, setApplicants] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [matchedCandidates, setMatchedCandidates] = useState([]);
  const [candidateDocuments, setCandidateDocuments] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [activeEmployees, setActiveEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const descRef = useRef();

  React.useEffect(() => {
    fetchCompanyProfile();
    fetchJobs();
    fetchMatchedCandidates();
    fetchActiveEmployees();
  }, []);

  const fetchCompanyProfile = async () => {
    try {
      const token = localStorage.getItem('gogig_token');
      const res = await fetch(`${API_URL}/api/company/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.success && data.company) {
        const updatedUser = { ...user, ...data.company, type: 'company' };
        setUser(updatedUser);
        localStorage.setItem('gogig_user', JSON.stringify(updatedUser));
      }
    } catch (error) {
      console.error('Failed to refresh company profile:', error);
    }
  };

  const fetchJobs = async () => {
    try {
      const token = localStorage.getItem('gogig_token');
      const res = await fetch(`${API_URL}/api/company/jobs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        throw new Error(`Failed to fetch jobs: ${res.status}`);
      }
      
      const data = await res.json();
      if (data.success) {
         const jobsList = data.jobs || [];
        setJobs(jobsList);
        
        // Fetch applicant counts for each job
        const counts = {};
        for (const job of jobsList) {
          try {
            const applicantsRes = await fetch(`${API_URL}/api/company/jobs/${job.id}/applicants`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (applicantsRes.ok) {
              const applicantsData = await applicantsRes.json();
              counts[job.id] = applicantsData.applicants?.length || 0;
            }
          } catch (err) {
            counts[job.id] = 0;
          }
        }
        setJobApplicantCounts(counts);
      } else {
        setJobs([]);
      }
    } catch (err) {
      console.error('Error fetching jobs:', err);
      // Don't show error to user for initial load - just set empty array
      setJobs([]);
    }
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleLogoChange = e => {
    const file = e.target.files[0];
    if (file) {
      setForm({ ...form, logo: file });
    }
  };

  const handleSave = async e => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const token = localStorage.getItem('gogig_token');
      const formData = new FormData();
      formData.append('company_name', form.company_name);
      formData.append('address', form.address);
      formData.append('email', form.email);
      formData.append('establishment_code', form.establishment_code);
      if (form.logo) {
        formData.append('logo', form.logo);
      }

      const res = await fetch(`${API_URL}/api/company/profile`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Update failed');
      
      // Update user with new data
      const updatedUser = { 
        ...user, 
        company_name: form.company_name,
        address: form.address,
        email: form.email,
        establishment_code: form.establishment_code,
        logo_url: data.logo_url || user.logo_url
      };
      setUser(updatedUser);
      localStorage.setItem('gogig_user', JSON.stringify(updatedUser));
      setSuccess('Profile updated!');
      setEdit(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleJobChange = e => {
    const { name, value } = e.target;
    setJobForm(f => ({ ...f, [name]: value }));
  };

  const handleQualChange = q => {
    setJobForm(f => ({ ...f, qualification: f.qualification.includes(q) ? f.qualification.filter(x => x !== q) : [...f.qualification, q] }));
  };

  const handleDescInput = e => {
    setJobForm(f => ({ ...f, description: e.target.innerHTML }));
  };

  const handleJobSubmit = async e => {
    e.preventDefault();
    setJobError(''); setJobSuccess('');
    if (!jobForm.title || !jobForm.description || !jobForm.age_criteria || !jobForm.stipend_min || !jobForm.stipend_max || !jobForm.location || jobForm.qualification.length === 0) {
      setJobError('All fields are required');
      return;
    }
    
    try {
      const token = localStorage.getItem('gogig_token');
      let res, data;
      
      if (editJobId) {
        res = await fetch(`${API_URL}/api/company/jobs/${editJobId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ ...jobForm, stipend_min: Number(jobForm.stipend_min), stipend_max: Number(jobForm.stipend_max), qualification: jobForm.qualification.join(',') })
        });
        
        if (!res.ok) {
          data = await res.json().catch(() => ({}));
          throw new Error(data.error || `Failed to update job: ${res.status}`);
        }
        
        data = await res.json();
        setJobSuccess('Job updated!');
      } else {
        res = await fetch(`${API_URL}/api/company/jobs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ ...jobForm, stipend_min: Number(jobForm.stipend_min), stipend_max: Number(jobForm.stipend_max), qualification: jobForm.qualification.join(',') })
        });
        
        if (!res.ok) {
          data = await res.json().catch(() => ({}));
          throw new Error(data.error || `Failed to post job: ${res.status}`);
        }
        
        data = await res.json();
        setJobSuccess('Job posted!');
      }
      
      setEditJobId(null);
      setShowJobForm(false);
      setJobForm({ title: '', description: '', age_criteria: '', stipend_min: '', stipend_max: '', location: '', qualification: [] });
      fetchJobs();
    } catch (err) {
      console.error('Error submitting job:', err);
      if (err.message === 'Failed to fetch' || err.name === 'TypeError') {
        setJobError('Cannot connect to server. Please ensure the backend server is running on port 5001.');
      } else {
        setJobError(err.message || 'Failed to save job. Please try again.');
      }
    }
  };

  const handleEditJob = job => {
    setEditJobId(job.id);
    setShowJobForm(true);
    setJobForm({
      title: job.title,
      description: job.details,
      age_criteria: job.age_criteria,
      stipend_min: job.stipend_min,
      stipend_max: job.stipend_max,
      location: job.location,
      qualification: job.qualification ? job.qualification.split(',') : [],
    });
    setJobError('');
    setJobSuccess('');
  };

  const handleDeleteJob = async jobId => {
    if (!window.confirm('Delete this job?')) return;
    try {
      const token = localStorage.getItem('gogig_token');
      const res = await fetch(`${API_URL}/api/company/jobs/${jobId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to delete job: ${res.status}`);
      }
      
      fetchJobs();
    } catch (err) {
      console.error('Error deleting job:', err);
      alert(err.message || 'Failed to delete job. Please check if the server is running.');
    }
  };

  const handleViewApplicants = async job => {
    setSelectedJob(job);
    setShowApplicants(true);
    try {
      const token = localStorage.getItem('gogig_token');
      const res = await fetch(`${API_URL}/api/company/jobs/${job.id}/applicants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        throw new Error(`Failed to fetch applicants: ${res.status}`);
      }
      
      const data = await res.json();
      if (data.success) {
        const applicantsList = data.applicants || [];
        setApplicants(applicantsList);
        // Update the applicant count for this job
        setJobApplicantCounts(prev => ({
          ...prev,
          [job.id]: applicantsList.length
        }));
      } else {
        setApplicants([]);
      }
    } catch (err) {
      console.error('Error fetching applicants:', err);
      setApplicants([]);
    }
  };

  const handleStatusChange = async (appId, status) => {
    try {
      const token = localStorage.getItem('gogig_token');
      const res = await fetch(`${API_URL}/api/company/applications/${appId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status })
      });
      
      if (res.ok) {
        setApplicants(applicants => applicants.map(a => a.id === appId ? { ...a, status } : a));
      } else {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to update status: ${res.status}`);
      }
    } catch (err) {
      console.error('Error updating applicant status:', err);
      alert(err.message || 'Failed to update status. Please try again.');
    }
  };

  const fetchMatchedCandidates = async () => {
    try {
      const token = localStorage.getItem('gogig_token');
      const res = await fetch(`${API_URL}/api/company/candidates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        throw new Error(`Failed to fetch candidates: ${res.status}`);
      }
      
      const data = await res.json();
      if (data.success) {
        setMatchedCandidates(data.candidates || []);
      } else {
        setMatchedCandidates([]);
      }
    } catch (err) {
      console.error('Error fetching matched candidates:', err);
      // Don't show error to user for initial load - just set empty array
      setMatchedCandidates([]);
    }
  };

  const fetchActiveEmployees = async () => {
    setEmployeesLoading(true);
    try {
      const token = localStorage.getItem('gogig_token');
      const currentYear = new Date().getFullYear();
      const res = await fetch(`${API_URL}/api/company/attrition-report?year=${currentYear}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        throw new Error(`Failed to fetch employees: ${res.status}`);
      }
      
      const data = await res.json();
      if (data.success && data.currentActiveEmployees) {
        setActiveEmployees(data.currentActiveEmployees);
      } else {
        setActiveEmployees([]);
      }
    } catch (error) {
      console.error('Error fetching active employees:', error);
      // Don't show error to user - just set empty array
      setActiveEmployees([]);
    } finally {
      setEmployeesLoading(false);
    }
  };

  const handleViewCandidateDocuments = async (candidate) => {
    setSelectedCandidate(candidate);
    try {
      const token = localStorage.getItem('gogig_token');
      const res = await fetch(`${API_URL}/api/company/candidates/${candidate.id}/documents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        throw new Error(`Failed to fetch documents: ${res.status}`);
      }
      
      const data = await res.json();
      if (data.success) {
        setCandidateDocuments(data.documents || []);
      } else {
        setCandidateDocuments([]);
      }
    } catch (err) {
      console.error('Error fetching candidate documents:', err);
      setCandidateDocuments([]);
    }
  };

  const totalApplicantsCount = Object.values(jobApplicantCounts).reduce((sum, n) => sum + (n || 0), 0);

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
    { id: 'jobs', label: 'Jobs', icon: <WorkIcon /> },
    { id: 'applicants', label: 'Applicants', icon: <PeopleIcon /> },
    { id: 'employees', label: 'Employees', icon: <PersonIcon /> },
    { id: 'unregistered', label: 'Unregistered', icon: <PendingIcon /> },
    { id: 'billing', label: 'Billing', icon: <ReceiptIcon /> },
    { id: 'attrition', label: 'Attrition Report', icon: <AssessmentIcon /> }
  ];

  const renderDashboard = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Overview Stats */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <CardContent sx={{ py: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: '#0d9488', mr: 2 }}>
                  <WorkIcon />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Posted Jobs
                  </Typography>
                  <Typography variant="h5" component="div" fontWeight={700} color="#0f766e">
                    {jobs.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <CardContent sx={{ py: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: '#059669', mr: 2 }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Matched Candidates
                  </Typography>
                  <Typography variant="h5" component="div" fontWeight={700} color="#047857">
                    {matchedCandidates.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <CardContent sx={{ py: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: '#0891b2', mr: 2 }}>
                  <PeopleIcon />
                </Avatar>
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    Total Applicants
                  </Typography>
                  <Typography variant="h5" component="div" fontWeight={700} color="#0e7490">
                    {totalApplicantsCount}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Company Profile - moderate radius + padding so title is never clipped */}
      <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.06)', overflow: 'visible' }}>
        <CardContent sx={{ px: 3, pt: 2.5, pb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
              Company Profile
            </Typography>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => setEdit(!edit)}
              sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
            >
              {edit ? 'Cancel' : 'Edit Profile'}
            </Button>
          </Box>
        
        {edit ? (
            <Box component="form" onSubmit={handleSave} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Company Name"
                    name="company_name"
                    value={form.company_name}
                    onChange={handleChange}
                    placeholder="Company Name"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="Email"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Establishment Code"
                    name="establishment_code"
                    value={form.establishment_code}
                    onChange={handleChange}
                    placeholder="Establishment Code"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    inputProps={{ accept: 'image/*' }}
                    helperText="Upload company logo"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    placeholder="Address"
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
              {form.logo_url && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <img src={getLogoUrl(form.logo_url)} alt="Logo" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }} />
                </Box>
              )}
              {error && <Typography color="error" variant="body2">{error}</Typography>}
              {success && <Typography color="success.main" variant="body2">{success}</Typography>}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setEdit(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {user.logo_url && (
                  <Avatar 
                    src={getLogoUrl(user.logo_url)} 
                    alt="Logo" 
                    sx={{ width: 56, height: 56 }}
                  />
                )}
                <Box>
                  <Typography variant="h6" component="h4">
                    {user.company_name || 'Company name not set'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user.email || 'Email not set'}
                  </Typography>
                </Box>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Mobile
                  </Typography>
                  <Typography variant="body1">
                    {user.mobile}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Establishment Code
                  </Typography>
                  <Chip 
                    label={user.establishment_code || 'Not set'} 
                    color="primary" 
                    size="small"
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Address
                  </Typography>
                  <Typography variant="body1">
                    {user.address || 'Not set'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  const renderJobs = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
            Job Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create and manage your job postings
          </Typography>
        </Box>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => { 
            setEditJobId(null); 
            setShowJobForm(true);
            setJobForm({ title: '', description: '', age_criteria: '', stipend_min: '', stipend_max: '', location: '', qualification: [] }); 
          }}
        >
          Post New Job
        </Button>
      </Box>

      {(editJobId !== null || showJobForm) && (
        <Card>
          <CardContent>
            <Typography variant="h6" component="h3" gutterBottom>
              {editJobId ? 'Edit Job' : 'Post New Job'}
            </Typography>
            <Box component="form" onSubmit={handleJobSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Job Title"
                    name="title"
                    value={jobForm.title}
                    onChange={handleJobChange}
                    placeholder="Job Title"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Location"
                    name="location"
                    value={jobForm.location}
                    onChange={handleJobChange}
                    placeholder="Location"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Age Criteria"
                    name="age_criteria"
                    value={jobForm.age_criteria}
                    onChange={handleJobChange}
                    placeholder="e.g. 18-25"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Min Stipend"
                        name="stipend_min"
                        type="number"
                        value={jobForm.stipend_min}
                        onChange={handleJobChange}
                        placeholder="Min"
                        InputProps={{
                          startAdornment: <Typography color="text.secondary" sx={{ mr: 1 }}>₹</Typography>
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Max Stipend"
                        name="stipend_max"
                        type="number"
                        value={jobForm.stipend_max}
                        onChange={handleJobChange}
                        placeholder="Max"
                        InputProps={{
                          startAdornment: <Typography color="text.secondary" sx={{ mr: 1 }}>₹</Typography>
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Job Description"
                    name="description"
                    value={jobForm.description}
                    onChange={handleJobChange}
                    multiline
                    rows={4}
                    placeholder="Enter job description"
                    helperText="You can use basic formatting like bold, italics, and bullet points"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Qualification Requirements
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {QUALIFICATIONS.map(q => (
                      <Chip
                        key={q}
                        label={q}
                        onClick={() => handleQualChange(q)}
                        color={jobForm.qualification.includes(q) ? 'primary' : 'default'}
                        variant={jobForm.qualification.includes(q) ? 'filled' : 'outlined'}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>

              {jobError && <Alert severity="error" sx={{ mt: 2 }}>{jobError}</Alert>}
              {jobSuccess && <Alert severity="success" sx={{ mt: 2 }}>{jobSuccess}</Alert>}

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  type="submit"
                  startIcon={editJobId ? <EditIcon /> : <AddIcon />}
                >
                  {editJobId ? 'Update Job' : 'Post Job'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => { 
                    setEditJobId(null); 
                    setShowJobForm(false);
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3}>
        {jobs.length === 0 ? (
          <Grid item xs={12}>
            <Card sx={{ textAlign: 'center', py: 6 }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No jobs posted yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create your first job posting to get started
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          jobs.map(job => (
            <Grid item xs={12} key={job.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" component="h3" gutterBottom>
                        {job.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          📍 {job.location}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          👤 {job.age_criteria}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          💰 ₹{job.stipend_min} - ₹{job.stipend_max}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button variant="outlined" size="small" onClick={() => handleEditJob(job)}>
                        Edit
                      </Button>
                      <Button variant="outlined" color="error" size="small" onClick={() => handleDeleteJob(job.id)}>
                        Delete
                      </Button>
                      <Badge 
                        badgeContent={jobApplicantCounts[job.id] || 0} 
                        color="primary"
                        invisible={!jobApplicantCounts[job.id] || jobApplicantCounts[job.id] === 0}
                      >
                        <Button 
                          size="small" 
                          onClick={() => handleViewApplicants(job)}
                          startIcon={<PeopleIcon />}
                        >
                          View Applicants
                        </Button>
                      </Badge>
                    </Box>
                  </Box>
                  <Box sx={{ mb: 2 }} dangerouslySetInnerHTML={{ __html: job.details }} />
                  <Chip label={`Qualification: ${job.qualification}`} color="primary" size="small" />
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>
    </Box>
  );

  const renderApplicants = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
          Job Applicants
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Review and manage applications for your jobs
        </Typography>
      </Box>
      {jobs.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No jobs posted yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Post a job to start receiving applications
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {jobs.map(job => (
            <Grid item xs={12} key={job.id}>
              <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h6" component="h3">
                      {job.title}
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<PeopleIcon />}
                      onClick={() => handleViewApplicants(job)}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      View Applicants ({jobApplicantCounts[job.id] ?? 0})
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );

  const renderEmployees = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Active Employees Section */}
      <Box>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, justifyContent: 'space-between', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
              Active Employees
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Employees receiving stipend this month ({new Date().toLocaleString('default', { month: 'long', year: 'numeric' })})
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Chip 
              icon={<CheckCircleIcon />}
              label={`${activeEmployees.length} Active`} 
              color="success" 
              variant="filled"
              size="medium"
            />
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchActiveEmployees}
              disabled={employeesLoading}
              size="small"
            >
              Refresh
            </Button>
          </Box>
        </Box>
        
        {employeesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : activeEmployees.length === 0 ? (
          <Card sx={{ textAlign: 'center', py: 6 }}>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No active employees this month
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Employees will appear here when stipend data is uploaded for this month
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {activeEmployees.map((employee, index) => (
              <Grid item xs={12} sm={6} md={4} key={employee.id || index}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar
                        sx={{
                          width: 48,
                          height: 48,
                          background: 'linear-gradient(135deg, #4caf50, #8bc34a)',
                          fontSize: '1.2rem',
                          fontWeight: 600
                        }}
                      >
                        {employee.name ? employee.name.charAt(0).toUpperCase() : '?'}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          {employee.name || 'Name not available'}
                        </Typography>
                        <Chip 
                          label={`${employee.tenure} month${employee.tenure !== 1 ? 's' : ''}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Employee Code
                        </Typography>
                        <Typography variant="body2">
                          {employee.employeeCode || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Mobile
                        </Typography>
                        <Typography variant="body2">
                          {employee.mobile || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Source
                        </Typography>
                        <Chip 
                          label={employee.source || 'unknown'} 
                          size="small"
                          color={employee.source === 'gogig' ? 'primary' : employee.source === 'eduvantage' ? 'secondary' : 'default'}
                          variant="outlined"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Divider />

      {/* Matched Employees Section */}
      <Box>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, justifyContent: 'space-between', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
              All Matched Employees
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Candidates automatically matched by establishment code
            </Typography>
          </Box>
          <Chip 
            icon={<DashboardIcon />}
            label={`Code: ${user.establishment_code || 'Not set'}`} 
            color="primary" 
            variant="outlined"
            size="medium"
          />
        </Box>
      
      {matchedCandidates.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 6 }}>
          <CardContent>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No matched candidates yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Candidates will appear here when they match your establishment code
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {matchedCandidates.map(candidate => (
            <Grid item xs={12} key={candidate.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar
                          sx={{
                            width: 56,
                            height: 56,
                            background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                            fontSize: '1.5rem',
                            fontWeight: 600
                          }}
                        >
                          {candidate.name ? candidate.name.charAt(0).toUpperCase() : '?'}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" gutterBottom>
                            {candidate.name || 'Name not available'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {candidate.email || 'Email not provided'}
                          </Typography>
                        </Box>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Mobile
                          </Typography>
                          <Typography variant="body2">
                            {candidate.mobile || 'Not provided'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Qualification
                          </Typography>
                          <Typography variant="body2">
                            {candidate.qualification || 'Not provided'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Course
                          </Typography>
                          <Typography variant="body2">
                            {candidate.course || 'Not provided'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Documents
                          </Typography>
                          <Typography variant="body2">
                            {candidate.document_count || 0} ({candidate.document_types || 'None'})
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>

                    <Button
                      variant="outlined"
                      onClick={() => handleViewCandidateDocuments(candidate)}
                      startIcon={<EditIcon />}
                      sx={{ ml: 2 }}
                    >
                      View Documents
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      </Box>
    </Box>
  );

  if (isStaffingCompany) {
    return <StaffingCompanyDashboard onLogout={onLogout} />;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f1f5f9' }}>
      {/* Header */}
      <AppBar 
        position="static" 
        elevation={0}
        sx={{
          background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 50%, #14b8a6 100%)',
          boxShadow: '0 4px 20px rgba(13, 148, 136, 0.25)'
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', fontWeight: 700, mr: 2 }}>
            {user.company_name ? user.company_name.charAt(0).toUpperCase() : 'G'}
          </Avatar>
          <Typography variant="h6" component="h1" sx={{ flexGrow: 1, color: '#fff', fontWeight: 700 }}>
            Company Dashboard
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" fontWeight="600" sx={{ color: '#fff' }}>
                {user.company_name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                {user.email}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<LogoutIcon />}
              onClick={onLogout}
              size="small"
              sx={{
                borderColor: 'rgba(255,255,255,0.8)',
                color: '#fff',
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.15)' }
              }}
            >
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Navigation tabs - scrollable, text never clipped */}
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 52,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 52,
              py: 1.5,
              px: 2,
              fontSize: '0.9rem',
            },
            '& .MuiTab-iconWrapper': { marginRight: 1, marginBottom: 0 },
            '& .Mui-selected': { color: '#0d9488' },
            '& .MuiTabs-indicator': { background: 'linear-gradient(90deg, #0d9488, #14b8a6)', height: 3, borderRadius: '3px 3px 0 0' },
            '& .MuiTabs-scroller': { overflow: 'auto !important' },
          }}
        >
          {tabs.map(tab => (
            <Tab
              key={tab.id}
              value={tab.id}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Box>

      {/* Main Content - visible area, no clipping */}
      <Container maxWidth="xl" sx={{ py: 3, minHeight: '60vh', overflow: 'visible' }}>
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'jobs' && renderJobs()}
        {activeTab === 'applicants' && renderApplicants()}
        {activeTab === 'employees' && renderEmployees()}
        {activeTab === 'unregistered' && <UnregisteredCandidates companyName={user.company_name} />}
        {activeTab === 'billing' && <VendorBilling companyName={user.company_name} />}
        {activeTab === 'attrition' && <AttritionReport companyName={user.company_name} />}
      </Container>

      {/* Applicants Modal */}
      <Dialog
        open={showApplicants}
        onClose={() => setShowApplicants(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Applicants for {selectedJob?.title}
            </Typography>
            <IconButton onClick={() => setShowApplicants(false)} size="small">
              <Typography>✕</Typography>
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {applicants.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No applicants yet.
              </Typography>
            </Box>
          ) : (
            <Box>
              <Paper sx={{ width: '100%', overflow: 'hidden', mb: 2 }}>
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5' }}>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: 600 }}>Name</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: 600 }}>Email</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: 600 }}>Mobile</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: 600 }}>Qualification</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: 600 }}>Course</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: 600 }}>Status</th>
                        <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #ddd', fontWeight: 600 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applicants.map((app, index) => (
                        <tr key={app.id} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>{app.name || '-'}</td>
                          <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>{app.email || '-'}</td>
                          <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>{app.mobile || '-'}</td>
                          <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>{app.qualification || '-'}</td>
                          <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>{app.course || '-'}</td>
                          <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                            <Chip
                              label={app.status || 'applied'}
                              size="small"
                              color={
                                app.status === 'selected' ? 'success' :
                                app.status === 'rejected' ? 'error' :
                                app.status === 'interview scheduled' ? 'warning' :
                                'default'
                              }
                            />
                          </td>
                          <td style={{ padding: '12px', borderBottom: '1px solid #eee' }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {['applied', 'selected', 'rejected', 'interview scheduled'].map(status => (
                                <Button
                                  key={status}
                                  variant={app.status === status ? 'contained' : 'outlined'}
                                  size="small"
                                  sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.7rem' }}
                                  color={
                                    status === 'selected' ? 'success' :
                                    status === 'rejected' ? 'error' :
                                    status === 'interview scheduled' ? 'warning' :
                                    'primary'
                                  }
                                  onClick={() => handleStatusChange(app.id, status)}
                                >
                                  {status.charAt(0).toUpperCase() + status.slice(1).replace(' ', '\n')}
                                </Button>
                              ))}
                            </Box>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </Paper>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Total: {applicants.length} applicant{applicants.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            color="primary"
            disabled={applicants.length === 0}
            onClick={() => {
              const header = ['Name', 'Email', 'Mobile', 'Qualification', 'Course', 'Status', 'Applied Date'];
              const rows = applicants.map(app => [
                app.name || '',
                app.email || '',
                app.mobile || '',
                app.qualification || '',
                app.course || '',
                app.status || 'applied',
                app.applied_at ? new Date(app.applied_at).toLocaleDateString() : ''
              ]);
              const csvContent = [header, ...rows]
                .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
                .join("\n");
              
              // Add BOM for Excel compatibility
              const BOM = '\uFEFF';
              const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              const jobTitle = selectedJob?.title?.replace(/[^a-z0-9]/gi, '_') || 'job';
              const dateStr = new Date().toISOString().split('T')[0];
              a.download = `applicants_${jobTitle}_${dateStr}.csv`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            }}
            startIcon={<GetAppIcon />}
          >
            Download CSV ({applicants.length})
          </Button>
          <Button
            variant="contained"
            onClick={() => setShowApplicants(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Documents Modal */}
      <Dialog
        open={Boolean(selectedCandidate)}
        onClose={() => { setSelectedCandidate(null); setCandidateDocuments([]); }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Documents for {selectedCandidate?.name}
            </Typography>
            <IconButton 
              onClick={() => { setSelectedCandidate(null); setCandidateDocuments([]); }}
              size="small"
            >
              <Typography>✕</Typography>
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body2">
                  {selectedCandidate?.email}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Mobile
                </Typography>
                <Typography variant="body2">
                  {selectedCandidate?.mobile}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Qualification
                </Typography>
                <Typography variant="body2">
                  {selectedCandidate?.qualification}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {candidateDocuments.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No documents found for this candidate.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {candidateDocuments.map(doc => (
                <Card key={doc.id} variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="medium">
                        Document Type: {doc.document_type}
                      </Typography>
                      {doc.CreatedAt && (
                        <Typography variant="caption" color="text.secondary">
                          {new Date(doc.CreatedAt).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>

                    <Grid container spacing={2}>
                      {doc.ContractCode && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Contract Code
                          </Typography>
                          <Typography variant="body2">
                            {doc.ContractCode}
                          </Typography>
                        </Grid>
                      )}
                      {doc.EstablishmentCode && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Establishment Code
                          </Typography>
                          <Typography variant="body2">
                            {doc.EstablishmentCode}
                          </Typography>
                        </Grid>
                      )}
                      {doc.CandidateName && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Candidate Name
                          </Typography>
                          <Typography variant="body2">
                            {doc.CandidateName}
                          </Typography>
                        </Grid>
                      )}
                      {doc.CandidateMobileNumber && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">
                            Mobile
                          </Typography>
                          <Typography variant="body2">
                            {doc.CandidateMobileNumber}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="contained"
            onClick={() => { setSelectedCandidate(null); setCandidateDocuments([]); }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CompanyDashboard; 